import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '../components/AppLayout';
import api from '../api/axiosInstance';
import { useAuth } from '../context/AuthContext';
import { extractStrategyRows, getActiveStrategies, normalizeStrategies } from '../api/strategyLibraryService';

const HEADER_H = 52;

const LEGACY_STRATEGY_SIGNALS = [
  { key: 'BottomFishing', label: 'Bottom Fishing' },
  { key: 'Parabolic', label: 'Parabolic' },
  { key: 'Exit', label: 'Exit' },
  { key: 'Fundamental', label: 'Fundamental' },
  { key: 'MFibo', label: 'M Fibo' },
  { key: 'RSC', label: 'RSC' },
  { key: 'DailyABCandlePositive', label: 'Daily AB Candle +ve' },
  { key: 'DailyABCandleNegative', label: 'Daily AB Candle -ve' },
];

const SIGNAL_ALIASES = {
  bottomfishing: ['BottomFishing', 'bottomFishing'],
  parabolic: ['Parabolic'],
  exit: ['Exit'],
  fundamental: ['Fundamental'],
  mfibo: ['MFibo', 'fibonacci'],
  rsc: ['RSC'],
  dailyabcandlepositive: ['DailyABCandlePositive', 'abPositive'],
  dailyabcandlenegative: ['DailyABCandleNegative', 'abNegative'],
};

const FILTERS = ['ALL', 'N50', 'FO', 'CASH', 'BULLISH', 'BEARISH'];

/* ─── Category Badge ─────────────────────────────────────────────── */
function CatBadge({ cat }) {
  if (!cat) return <span style={{ color: 'rgba(255,255,255,0.3)' }}>—</span>;
  if (cat === 'N50') return <span style={S.badgeN50}>N50</span>;
  if (cat === 'FO') return <span style={S.badgeFO}>FO</span>;
  return <span style={S.badgeOthers}>CASH</span>;
}

/* ─── Main Component ─────────────────────────────────────────────── */
export default function WatchlistTracker() {
  const { user } = useAuth();

  const [watchlists, setWatchlists]     = useState([]);
  const [selectedId, setSelectedId]     = useState('');
  const [stocksData, setStocksData]     = useState([]);
  const [watchlistName, setWatchlistName] = useState('');
  const [watchlistColor, setWatchlistColor] = useState('#667eea');
  const [loading, setLoading]           = useState(false);
  const [filter, setFilter]             = useState('ALL');
  const [expandedRow, setExpandedRow]   = useState(null);
  const [editingRow, setEditingRow]     = useState(null);
  const [editVals, setEditVals]         = useState({});
  const [savingEdit, setSavingEdit]     = useState(false);
  const [lastUpdated, setLastUpdated]   = useState(null);
  const [toast, setToast]               = useState({ msg: '', type: 'success' });
  const [strategyMeta, setStrategyMeta] = useState(LEGACY_STRATEGY_SIGNALS);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: '', type: 'success' }), 3000);
  };

  /* ── load watchlist list ── */
  useEffect(() => {
    if (!user?.id) return;
    api.get(`/watchlist/user/${user.id}`)
      .then(({ data }) => {
        const list = data.data || [];
        setWatchlists(list);
        if (list.length > 0) {
          setSelectedId(list[0].watchlistId);
          setWatchlistColor(list[0].color || '#667eea');
        }
      })
      .catch(() => {});
  }, [user?.id]);

  /* ── fetch market data ── */
  const fetchData = useCallback(async () => {
    if (!selectedId) return;
    setLoading(true);
    setExpandedRow(null);
    setEditingRow(null);
    try {
      const { data } = await api.get(`/watchlist/${selectedId}/data`);
      setStocksData(data.data || []);
      setWatchlistName(data.watchlistName || '');
      setLastUpdated(new Date());
    } catch {
      showToast('Failed to load market data', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    let mounted = true;
    getActiveStrategies()
      .then(({ data }) => {
        if (!mounted) return;
        const rows = normalizeStrategies(extractStrategyRows(data)).filter(s => s.slug);
        if (rows.length) setStrategyMeta(rows.map(s => ({ key: s.slug, label: s.name })));
      })
      .catch(() => {})
    return () => { mounted = false; };
  }, []);

  const getStrategySignals = (signals = {}) => {
    const configured = strategyMeta.map(strategy => ({
      ...strategy,
      active: [strategy.key, ...(SIGNAL_ALIASES[strategy.key] || [])].some(key => !!signals[key]),
    }));
    const configuredKeys = new Set(configured.flatMap(strategy => [strategy.key, ...(SIGNAL_ALIASES[strategy.key] || [])]));
    const extras = Object.keys(signals)
      .filter(key => key !== 'totalSignals' && !configuredKeys.has(key))
      .map(key => ({ key, label: key, active: !!signals[key] }));
    return [...configured, ...extras];
  };

  /* ── watchlist selector change ── */
  const handleSelectChange = (e) => {
    const id = e.target.value;
    setSelectedId(id);
    const wl = watchlists.find(w => w.watchlistId === id);
    if (wl) setWatchlistColor(wl.color || '#667eea');
  };

  /* ── filter ── */
  const filtered = stocksData.filter(s => {
    if (filter === 'ALL') return true;
    if (filter === 'N50') return s.dataSource === 'N50';
    if (filter === 'FO') return s.dataSource === 'FO';
    if (filter === 'CASH') return s.dataSource === 'CASH' || s.dataSource === 'OTHERS';
    if (filter === 'BULLISH') return s.percentChange !== null && s.percentChange > 0;
    if (filter === 'BEARISH') return s.percentChange !== null && s.percentChange < 0;
    return true;
  });

  /* ── analytics ── */
  const total        = stocksData.length;
  const positive     = stocksData.filter(s => s.percentChange > 0).length;
  const negative     = stocksData.filter(s => s.percentChange < 0).length;
  const nearTarget   = stocksData.filter(s => s.targetDistance !== null && parseFloat(s.targetDistance) >= 0 && parseFloat(s.targetDistance) <= 5).length;
  const nearSL       = stocksData.filter(s => s.stopLossDistance !== null && parseFloat(s.stopLossDistance) >= -2 && parseFloat(s.stopLossDistance) <= 5).length;
  const withSignals  = stocksData.filter(s => s.strategySignals?.totalSignals > 0).length;

  /* ── inline edit save ── */
  const saveStockEdit = async (symbol) => {
    setSavingEdit(true);
    try {
      const payload = {};
      if (editVals.entryPrice !== '') payload.entryPrice = editVals.entryPrice;
      if (editVals.targetPrice !== '') payload.targetPrice = editVals.targetPrice;
      if (editVals.stopLoss !== '') payload.stopLoss = editVals.stopLoss;
      await api.put(`/watchlist/${selectedId}/update-stock/${symbol}`, payload);
      setEditingRow(null);
      fetchData();
      showToast(`${symbol} updated!`);
    } catch {
      showToast('Update failed', 'error');
    } finally {
      setSavingEdit(false);
    }
  };

  /* ── helpers ── */
  const getStatus = (s) => {
    if (!s.currentPrice) return null;
    if (s.targetPrice && s.currentPrice >= s.targetPrice) return 'target';
    if (s.stopLoss && s.currentPrice <= s.stopLoss) return 'stoploss';
    return 'active';
  };

  const fmt = (v, prefix = '₹') => (v !== null && v !== undefined ? `${prefix}${parseFloat(v).toFixed(2)}` : '—');
  const fmtPct = (v, showSign = true) => {
    if (v === null || v === undefined) return '—';
    const n = parseFloat(v);
    return `${showSign && n > 0 ? '+' : ''}${n.toFixed(2)}%`;
  };

  /* ─── render ─────────────────────────────────────────────────────── */
  return (
    <AppLayout pageBadge="📊 Watchlist Tracker">
      <div style={S.bg} />

      {/* Toast */}
      {toast.msg && (
        <div style={{ ...S.toast, background: toast.type === 'error' ? 'rgba(239,68,68,0.95)' : 'rgba(16,185,129,0.95)' }}>
          {toast.msg}
        </div>
      )}

      <div style={S.shell}>
        {/* ── Top bar ── */}
        <div style={S.topBar}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {watchlistColor && (
              <div style={{ width: 10, height: 36, borderRadius: 5, background: watchlistColor, flexShrink: 0 }} />
            )}
            <div>
              <div style={S.pageTitle}>📊 Watchlist Tracker</div>
              {watchlistName && (
                <div style={S.pageSub}>Tracking: <strong style={{ color: 'rgba(255,255,255,0.75)' }}>{watchlistName}</strong></div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <select style={S.select} value={selectedId} onChange={handleSelectChange}>
              <option value="" style={{ background: '#1e2340' }}>— Select Watchlist —</option>
              {watchlists.map(w => (
                <option key={w.watchlistId} value={w.watchlistId} style={{ background: '#1e2340' }}>
                  {w.watchlistName} ({w.stocks?.length || 0})
                </option>
              ))}
            </select>
            <button style={S.btnRefresh} onClick={fetchData} disabled={!selectedId || loading}>
              {loading ? '⟳' : '⟳ Refresh'}
            </button>
          </div>
        </div>

        {/* ── Analytics bar ── */}
        {total > 0 && (
          <div style={S.analyticsBar}>
            {[
              { val: total,       lbl: 'Total',        grad: 'linear-gradient(135deg,#667eea,#764ba2)' },
              { val: positive,    lbl: '↑ Positive',   grad: 'linear-gradient(135deg,#00f260,#0575e6)' },
              { val: negative,    lbl: '↓ Negative',   grad: 'linear-gradient(135deg,#ff6b6b,#ee5a6f)' },
              { val: nearTarget,  lbl: 'Near Target',  grad: 'linear-gradient(135deg,#43e97b,#38f9d7)' },
              { val: nearSL,      lbl: 'Near S/L',     grad: 'linear-gradient(135deg,#f093fb,#f5576c)' },
              { val: withSignals, lbl: 'Signals Active',grad: 'linear-gradient(135deg,#4facfe,#00f2fe)' },
            ].map(({ val, lbl, grad }) => (
              <div key={lbl} style={S.analyticsCard}>
                <div style={{ ...S.analyticsVal, background: grad, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                  {val}
                </div>
                <div style={S.analyticsLbl}>{lbl}</div>
              </div>
            ))}
          </div>
        )}

        {/* ── Filter bar ── */}
        {total > 0 && (
          <div style={S.filterBar}>
            {FILTERS.map(f => (
              <button
                key={f}
                style={{ ...S.filterBtn, ...(filter === f ? S.filterBtnActive : {}) }}
                onClick={() => setFilter(f)}
              >
                {f}
              </button>
            ))}
            <span style={{ marginLeft: 'auto', fontSize: '0.73rem', color: 'rgba(255,255,255,0.35)' }}>
              {filtered.length}/{total} stocks
            </span>
          </div>
        )}

        {/* ── Table area ── */}
        <div style={S.tableWrap}>
          {loading ? (
            <div style={S.center}>
              <div style={S.spinner} />
              <div style={{ color: 'rgba(255,255,255,0.5)', marginTop: 12 }}>Loading market data...</div>
            </div>
          ) : !selectedId ? (
            <div style={S.center}>
              <div style={{ fontSize: 52, opacity: 0.2, marginBottom: 12 }}>📊</div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.95rem' }}>Select a watchlist to start tracking</div>
            </div>
          ) : filtered.length === 0 ? (
            <div style={S.center}>
              <div style={{ fontSize: 52, opacity: 0.2, marginBottom: 12 }}>📭</div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.95rem' }}>
                {stocksData.length === 0 ? 'No stocks in this watchlist. Add stocks in the Watchlist Manager.' : 'No stocks match the selected filter.'}
              </div>
            </div>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Symbol</th>
                  <th style={S.th}>Name</th>
                  <th style={S.th}>Cat</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Price</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Chng%</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>CS Value</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Entry</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Target</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>S/L</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>T-Dist</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>SL-Dist</th>
                  <th style={{ ...S.th, textAlign: 'center' }}>Signals</th>
                  <th style={{ ...S.th, textAlign: 'center' }}>Status</th>
                  <th style={{ ...S.th, width: 24 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(s => {
                  const pct     = s.percentChange;
                  const status  = getStatus(s);
                  const expanded = expandedRow === s.symbol;
                  const editing  = editingRow === s.symbol;

                  return (
                    <React.Fragment key={s.symbol}>
                      <tr
                        style={{ ...S.tr, ...(expanded ? S.trExpanded : {}) }}
                        onClick={() => {
                          setExpandedRow(expanded ? null : s.symbol);
                          if (editing && !expanded) setEditingRow(null);
                        }}
                      >
                        <td style={S.td}>
                          <span style={{ fontWeight: 700, color: '#fff', fontSize: '0.88rem' }}>{s.symbol}</span>
                        </td>
                        <td style={{ ...S.td, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.78rem', color: 'rgba(255,255,255,0.65)' }}>
                          {s.stockName}
                        </td>
                        <td style={S.td}><CatBadge cat={s.dataSource} /></td>
                        <td style={{ ...S.td, textAlign: 'right', fontWeight: 600 }}>
                          {s.currentPrice ? `₹${s.currentPrice.toFixed(2)}` : '—'}
                        </td>
                        <td style={{ ...S.td, textAlign: 'right', fontWeight: 700, color: pct > 0 ? '#00f260' : pct < 0 ? '#ff6b6b' : 'rgba(255,255,255,0.4)' }}>
                          {pct !== null && pct !== undefined ? fmtPct(pct) : '—'}
                        </td>
                        <td style={{ ...S.td, textAlign: 'right', fontSize: '0.8rem', color: 'rgba(255,255,255,0.65)' }}>
                          {s.csValue !== null && s.csValue !== undefined ? s.csValue.toLocaleString('en-IN') : '—'}
                        </td>
                        <td style={{ ...S.td, textAlign: 'right', fontSize: '0.8rem' }}>{fmt(s.entryPrice)}</td>
                        <td style={{ ...S.td, textAlign: 'right', fontSize: '0.8rem', color: '#43e97b' }}>{fmt(s.targetPrice)}</td>
                        <td style={{ ...S.td, textAlign: 'right', fontSize: '0.8rem', color: '#ff6b6b' }}>{fmt(s.stopLoss)}</td>
                        <td style={{ ...S.td, textAlign: 'right', fontSize: '0.8rem', color: s.targetDistance !== null ? (parseFloat(s.targetDistance) > 0 ? '#43e97b' : '#ff6b6b') : 'rgba(255,255,255,0.3)' }}>
                          {s.targetDistance !== null && s.targetDistance !== undefined ? `${s.targetDistance}%` : '—'}
                        </td>
                        <td style={{ ...S.td, textAlign: 'right', fontSize: '0.8rem', color: s.stopLossDistance !== null ? (parseFloat(s.stopLossDistance) > 0 ? '#4facfe' : '#ff6b6b') : 'rgba(255,255,255,0.3)' }}>
                          {s.stopLossDistance !== null && s.stopLossDistance !== undefined ? `${s.stopLossDistance}%` : '—'}
                        </td>
                        <td style={{ ...S.td, textAlign: 'center' }}>
                          <span style={{
                            ...S.signalBadge,
                            background: s.strategySignals?.totalSignals > 0
                              ? 'linear-gradient(135deg,#667eea,#764ba2)'
                              : 'rgba(255,255,255,0.08)',
                          }}>
                            {s.strategySignals?.totalSignals || 0}
                          </span>
                        </td>
                        <td style={{ ...S.td, textAlign: 'center' }}>
                          {status === 'target'   && <span style={S.statusTarget}>🎯 Target</span>}
                          {status === 'stoploss' && <span style={S.statusSL}>🛑 S/L</span>}
                          {status === 'active'   && <span style={S.statusActive}>● Active</span>}
                          {!status && '—'}
                        </td>
                        <td style={{ ...S.td, textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: '0.65rem' }}>
                          {expanded ? '▲' : '▼'}
                        </td>
                      </tr>

                      {/* Expanded details row */}
                      {expanded && (
                        <tr>
                          <td colSpan={14} style={S.detailsTd}>
                            <div style={S.detailsGrid}>

                              {/* Strategy signals */}
                              <div style={{ ...S.detailCard, gridColumn: 'span 2' }}>
                                <div style={S.detailLbl}>Strategy Signals ({s.strategySignals?.totalSignals || 0} active)</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8 }}>
                                  {getStrategySignals(s.strategySignals).map(({ key, label, active }) => {
                                    return (
                                      <span key={key} style={active ? S.sigActive : S.sigInactive}>{label}</span>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* P&L */}
                              <div style={S.detailCard}>
                                <div style={S.detailLbl}>Unrealized P&L</div>
                                {s.profitLoss !== null && s.profitLoss !== undefined ? (
                                  <div style={{ fontSize: '1.4rem', fontWeight: 700, marginTop: 6, color: parseFloat(s.profitLoss) >= 0 ? '#00f260' : '#ff6b6b' }}>
                                    {parseFloat(s.profitLoss) >= 0 ? '+' : ''}{s.profitLoss}%
                                  </div>
                                ) : (
                                  <div style={{ color: 'rgba(255,255,255,0.35)', marginTop: 6, fontSize: '0.85rem' }}>No entry price set</div>
                                )}
                              </div>

                              {/* Notes */}
                              <div style={S.detailCard}>
                                <div style={S.detailLbl}>Notes</div>
                                <div style={{ fontSize: '0.83rem', color: s.notes ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.3)', marginTop: 6 }}>
                                  {s.notes || 'No notes'}
                                </div>
                              </div>

                              {/* Inline edit panel */}
                              {editing ? (
                                <div style={{ ...S.detailCard, gridColumn: 'span 2' }}>
                                  <div style={S.detailLbl}>Edit Price Levels</div>
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 10 }}>
                                    {[
                                      { field: 'entryPrice', label: 'Entry Price ₹' },
                                      { field: 'targetPrice', label: 'Target Price ₹' },
                                      { field: 'stopLoss', label: 'Stop Loss ₹' },
                                    ].map(({ field, label }) => (
                                      <div key={field}>
                                        <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>{label}</div>
                                        <input
                                          style={S.inlineInput}
                                          type="number"
                                          placeholder="0.00"
                                          value={editVals[field] || ''}
                                          onChange={e => setEditVals(v => ({ ...v, [field]: e.target.value }))}
                                          onClick={e => e.stopPropagation()}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                                    <button
                                      style={S.btnSmall}
                                      onClick={(e) => { e.stopPropagation(); setEditingRow(null); }}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      style={{ ...S.btnSmall, background: 'linear-gradient(135deg,#00f260,#0575e6)', border: 'none' }}
                                      onClick={(e) => { e.stopPropagation(); saveStockEdit(s.symbol); }}
                                      disabled={savingEdit}
                                    >
                                      {savingEdit ? 'Saving...' : 'Save Changes'}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div style={S.detailCard}>
                                  <div style={S.detailLbl}>Actions</div>
                                  <button
                                    style={{ ...S.btnSmall, marginTop: 8 }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingRow(s.symbol);
                                      setEditVals({
                                        entryPrice: s.entryPrice || '',
                                        targetPrice: s.targetPrice || '',
                                        stopLoss: s.stopLoss || '',
                                      });
                                    }}
                                  >
                                    ✏️ Edit Levels
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Last updated */}
        {lastUpdated && (
          <div style={{ textAlign: 'center', fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', padding: '5px 0 8px', flexShrink: 0 }}>
            Last updated: {lastUpdated.toLocaleTimeString('en-IN')}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

/* ─── Styles ─────────────────────────────────────────────────────── */
const S = {
  bg: {
    position: 'fixed', inset: 0, zIndex: 0,
    background: 'linear-gradient(135deg, #0d0d1a 0%, #111827 50%, #0a1628 100%)',
    pointerEvents: 'none',
  },
  shell: {
    position: 'relative', zIndex: 1,
    height: `calc(100vh - ${HEADER_H}px)`,
    marginTop: HEADER_H,
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
  },
  toast: {
    position: 'fixed', top: 72, right: 24, zIndex: 9999,
    color: '#fff', padding: '0.75rem 1.25rem', borderRadius: 8,
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)', fontSize: '0.9rem',
    maxWidth: 380, backdropFilter: 'blur(10px)',
  },
  topBar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)',
    flexShrink: 0, gap: 12,
  },
  pageTitle: {
    fontSize: '1.2rem', fontWeight: 700,
    background: 'linear-gradient(135deg,#667eea,#764ba2)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  },
  pageSub: { fontSize: '0.76rem', color: 'rgba(255,255,255,0.4)', marginTop: 1 },
  select: {
    background: '#141428', color: '#fff',
    border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8,
    padding: '7px 12px', fontSize: '0.85rem', cursor: 'pointer', outline: 'none',
    minWidth: 200,
  },
  btnRefresh: {
    background: 'rgba(102,126,234,0.15)', color: '#a78bfa',
    border: '1px solid rgba(102,126,234,0.3)', borderRadius: 8,
    padding: '7px 14px', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem',
  },
  analyticsBar: {
    display: 'flex', gap: 10, padding: '10px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    flexShrink: 0, flexWrap: 'wrap',
  },
  analyticsCard: {
    flex: 1, minWidth: 90,
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 10, padding: '8px 12px', textAlign: 'center',
  },
  analyticsVal: {
    fontSize: '1.5rem', fontWeight: 700,
  },
  analyticsLbl: { fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', marginTop: 2 },
  filterBar: {
    display: 'flex', gap: 6, padding: '8px 20px', alignItems: 'center',
    borderBottom: '1px solid rgba(255,255,255,0.05)', flexShrink: 0, flexWrap: 'wrap',
  },
  filterBtn: {
    background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.6)',
    padding: '5px 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.08)',
    cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
  },
  filterBtnActive: {
    background: 'linear-gradient(135deg,#00f260,#0575e6)',
    color: '#fff', borderColor: 'transparent',
  },
  tableWrap: {
    flex: 1, minHeight: 0, overflowY: 'auto',
    overflowX: 'auto',
  },
  table: { width: '100%', borderCollapse: 'separate', borderSpacing: 0, minWidth: 900 },
  th: {
    padding: '10px 12px', textAlign: 'left',
    fontWeight: 600, color: 'rgba(255,255,255,0.85)',
    borderBottom: '2px solid rgba(102,126,234,0.25)',
    fontSize: '0.8rem', whiteSpace: 'nowrap',
    position: 'sticky', top: 0,
    background: 'rgba(10,10,28,0.96)', backdropFilter: 'blur(10px)', zIndex: 5,
  },
  tr: { cursor: 'pointer', transition: 'background 0.15s' },
  trExpanded: { background: 'rgba(102,126,234,0.1)' },
  td: {
    padding: '8px 12px', color: 'rgba(255,255,255,0.75)',
    borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: '0.83rem',
  },
  detailsTd: {
    padding: 0, borderBottom: '2px solid rgba(102,126,234,0.2)',
    background: 'rgba(102,126,234,0.05)',
  },
  detailsGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 12, padding: '14px 16px',
  },
  detailCard: {
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 8, padding: '10px 12px',
  },
  detailLbl: { fontSize: '0.73rem', color: 'rgba(255,255,255,0.45)', fontWeight: 600 },
  center: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', height: '100%', minHeight: 300, textAlign: 'center', padding: 24,
  },
  spinner: {
    width: 38, height: 38,
    border: '3px solid rgba(255,255,255,0.07)',
    borderTop: '3px solid #667eea',
    borderRadius: '50%', animation: 'spin 1s linear infinite',
  },
  badgeN50: {
    padding: '2px 7px', borderRadius: 4, fontSize: '0.63rem', fontWeight: 700,
    background: 'linear-gradient(135deg,#00f260,#0575e6)', color: '#fff',
  },
  badgeFO: {
    padding: '2px 7px', borderRadius: 4, fontSize: '0.63rem', fontWeight: 700,
    background: 'linear-gradient(135deg,#4facfe,#00f2fe)', color: '#fff',
  },
  badgeOthers: {
    padding: '2px 7px', borderRadius: 4, fontSize: '0.63rem', fontWeight: 700,
    background: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.85)',
    border: '1px solid rgba(255,255,255,0.18)',
  },
  signalBadge: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    minWidth: 26, height: 26, padding: '0 6px',
    borderRadius: 6, fontWeight: 700, fontSize: '0.82rem', color: '#fff',
  },
  statusTarget: {
    padding: '2px 8px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 700,
    background: 'rgba(0,242,96,0.15)', color: '#00f260',
    border: '1px solid rgba(0,242,96,0.25)',
  },
  statusSL: {
    padding: '2px 8px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 700,
    background: 'rgba(255,107,107,0.15)', color: '#ff6b6b',
    border: '1px solid rgba(255,107,107,0.25)',
  },
  statusActive: {
    padding: '2px 8px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 700,
    background: 'rgba(79,172,254,0.15)', color: '#4facfe',
    border: '1px solid rgba(79,172,254,0.25)',
  },
  sigActive: {
    padding: '3px 8px', borderRadius: 4, fontSize: '0.7rem',
    background: 'rgba(0,242,96,0.12)', color: '#00f260',
    border: '1px solid rgba(0,242,96,0.25)',
  },
  sigInactive: {
    padding: '3px 8px', borderRadius: 4, fontSize: '0.7rem',
    background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.3)',
    border: '1px solid rgba(255,255,255,0.08)',
  },
  inlineInput: {
    width: '100%', padding: '6px 8px', boxSizing: 'border-box',
    background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(102,126,234,0.4)',
    borderRadius: 6, color: '#fff', fontSize: '0.85rem', outline: 'none',
  },
  btnSmall: {
    padding: '5px 12px', borderRadius: 6,
    background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)',
    color: '#fff', fontSize: '0.8rem', cursor: 'pointer', fontWeight: 600,
  },
};
